import { BACKFILL_COLUMNS } from '@uit/shared/schemas/backfill';
import { useState } from 'react';
import { ReferralBackfillSection } from '../components/backfill/ReferralBackfillSection.js';
import s from './BackfillPage.module.scss';

interface InvalidRow {
  row_number: number;
  errors: string[];
}
interface PreviewResult {
  rows_in: number;
  rows_valid: number;
  rows_invalid: number;
  invalid: InvalidRow[];
}

function downloadCsv(filename: string, csv: string): void {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function csvCell(v: string): string {
  return /[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
}

export function BackfillPage() {
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<PreviewResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<{ imported: number; skipped: number } | null>(null);

  function handleTemplate(): void {
    downloadCsv('contractor_backfill_template.csv', `${BACKFILL_COLUMNS.join(',')}\n`);
  }

  function handleErrorReport(): void {
    if (!preview) return;
    const lines = [
      'row_number,errors',
      ...preview.invalid.map((r) => `${r.row_number},${csvCell(r.errors.join('; '))}`),
    ];
    downloadCsv('backfill_errors.csv', lines.join('\n') + '\n');
  }

  async function handlePreview(): Promise<void> {
    if (!file) return;
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await fetch('/api/backfill/preview', {
        method: 'POST',
        credentials: 'include',
        body: fd,
      });
      if (!r.ok) {
        setError(`Preview failed (${r.status}).`);
        return;
      }
      setPreview((await r.json()) as PreviewResult);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  async function handleImport(): Promise<void> {
    if (!file || !preview) return;
    setBusy(true);
    setError(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await fetch('/api/backfill/import', {
        method: 'POST',
        credentials: 'include',
        body: fd,
      });
      if (!r.ok) {
        setError(`Import failed (${r.status}).`);
        return;
      }
      setResult((await r.json()) as { imported: number; skipped: number });
      setPreview(null);
      setFile(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className={s.page}>
      <h1 className={s.title}>Backfill</h1>
      <p className={s.subtitle}>
        Bulk-load vendor + resource records, or backfill referral relationships, from CSV.
      </p>

      <section className={s.section}>
        <h2 className={s.sectionTitle}>Backfill vendors</h2>
        <p className={s.sectionNote}>
          Bulk-load vendor + resource records from a CSV (structured fields only — no documents).
          Download the template, fill it in, upload, then import the valid rows.
        </p>
        <div className={s.controls}>
          <button type="button" className={s.secondaryBtn} onClick={handleTemplate}>
            Download template
          </button>
          <input
            id="backfill-file"
            type="file"
            accept=".csv,text/csv"
            className={s.input}
            onChange={(e) => {
              setFile(e.target.files?.[0] ?? null);
              setPreview(null);
              setResult(null);
            }}
          />
          <button
            type="button"
            className={s.previewBtn}
            disabled={!file || busy}
            onClick={() => void handlePreview()}
          >
            {busy ? 'Working…' : 'Preview'}
          </button>
        </div>
        {error && (
          <div className={s.error} role="alert">
            {error}
          </div>
        )}
        {result && (
          <div className={s.info} role="status">
            Imported {result.imported} vendor{result.imported === 1 ? '' : 's'}; skipped{' '}
            {result.skipped}.
          </div>
        )}
      </section>

      {preview && (
        <>
          <section className={s.section}>
            <div className={s.counts}>
              <span>
                <strong>{preview.rows_in}</strong> rows in
              </span>
              <span>
                <strong>{preview.rows_valid}</strong> valid
              </span>
              <span>
                <strong>{preview.rows_invalid}</strong> invalid
              </span>
            </div>
          </section>

          {preview.invalid.length > 0 && (
            <section className={s.section}>
              <div className={s.sectionHead}>
                <h2 className={s.sectionTitle}>Invalid rows</h2>
                <button type="button" className={s.secondaryBtn} onClick={handleErrorReport}>
                  Download error report
                </button>
              </div>
              <p className={s.sectionNote}>
                Fix these in your source CSV and re-upload. Valid rows can still be imported now.
              </p>
              <table className={s.table}>
                <thead>
                  <tr>
                    <th scope="col">Row</th>
                    <th scope="col">Errors</th>
                  </tr>
                </thead>
                <tbody>
                  {preview.invalid.map((r) => (
                    <tr key={r.row_number}>
                      <td>{r.row_number}</td>
                      <td>
                        <ul className={s.errs}>
                          {r.errors.map((e) => (
                            <li key={e}>{e}</li>
                          ))}
                        </ul>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}

          <div className={s.footer}>
            <button
              type="button"
              className={s.importBtn}
              disabled={preview.rows_valid === 0 || busy}
              onClick={() => void handleImport()}
            >
              {busy
                ? 'Working…'
                : `Import ${preview.rows_valid} valid, skip ${preview.rows_invalid}`}
            </button>
          </div>
        </>
      )}

      <ReferralBackfillSection />
    </div>
  );
}
